import { BaseComponentImpl } from '../types/implementations';
import { Building } from '../types/protocols';
import { MaintenanceIntervalType, CardinalDirection } from '../types/Enums';
import { ComponentLifetime, MaintenanceTemplate, getLifetimeForDirection, getIntervalForDirection } from '../Pages/Förvaltning/TemplatesEditor/types';
import { addInterval } from './DateUtils';
import {
    ComponentMaintenanceData,
    CostSource,
    customRateOf,
    unitCountOf,
    getActivityDisplayCost,
    getReplacementDisplayCost,
    formatDateToString
} from './maintenanceCostUtils';
import { calculateReplacementDate, calculateNextMaintenanceDate } from '../Pages/Förvaltning/MaintenanceDraft/utils/calculations';
import { resolveCardinalDirection } from '../Pages/Förvaltning/MaintenanceDraft/utils/cardinalDirectionResolver';
import { templateOfferedTo } from './maintenanceTemplates';

/**
 * The one interpretation of a component's maintenance schedule.
 *
 * A draft or active plan stores per component a replacement year and a map of
 * maintenance activities (`ComponentMaintenanceData`). What that means on a
 * calendar — which years get a replacement, which dates get each activity,
 * what each costs — used to be derived separately by the component card, the
 * draft preview and plan activation, and the three disagreed. Every reader now
 * goes through `resolveComponentSchedule` so they cannot drift again.
 *
 * Rules, in the order the old readers disagreed on them:
 * - Templates are found by id, not only among the component type's own
 *   templates: an activity added from the template search keeps working.
 * - Values the entry lacks but that can be derived are derived, never dropped:
 *   next date from the condition, interval from the template, replacement year
 *   from the condition.
 * - A replacement year or a maintenance date before the window is overdue and
 *   lands in the first year of the window (the date keeps its month and day),
 *   so the component's whole cycle is not lost and the schedule runs on from
 *   the overdue occurrence.
 * - Direction is resolved through the parent chain (`resolveCardinalDirection`).
 * - In a draft, schablon costs (template or own unit price) are computed live
 *   (custom quantity included); in an active plan the snapshot taken at
 *   activation wins.
 * - In a draft, the type's templates without an entry are scheduled with
 *   defaults, as the card has always shown them; an active plan is only what
 *   was activated.
 * - A template in the entry's `removedActivities` is never scheduled.
 */

export interface ScheduleWindow {
    /** First year included (normally the current year) */
    startYear: number;
    /** First year excluded */
    endYear: number;
}

export interface ResolveScheduleOptions {
    component: BaseComponentImpl;
    entry: ComponentMaintenanceData;
    building: Building;
    lifetime: ComponentLifetime | undefined;
    /** The component type's own templates (building-specific ones of other buildings already removed) */
    typeTemplates: MaintenanceTemplate[];
    /** Lookup for activities whose template is not among `typeTemplates` */
    findTemplate: (templateId: string) => MaintenanceTemplate | undefined;
    window: ScheduleWindow;
    /** Draft: live template costs and implicit type templates. Active plan: stored snapshot only. */
    isDraft: boolean;
}

export interface ResolvedReplacement {
    /** Every replacement year inside the window, first cycle first */
    years: number[];
    /** The first replacement year, after the overdue clamp, even when outside the window */
    firstYear: number;
    expectedLifetimeYears: number;
    cost: number;
    costSource: CostSource;
    costComment?: string;
    costCalculation: string;
    investmentPercentage?: number;
}

export interface ResolvedActivity {
    templateId: string;
    template: MaintenanceTemplate;
    intervalValue: number;
    intervalType: MaintenanceIntervalType;
    /** The first due date (YYYY-MM-DD), stored or derived, which may lie before the window */
    firstDate: string;
    /** Due dates (YYYY-MM-DD) inside the window, in order */
    dates: string[];
    cost: number;
    costSource: CostSource;
    costComment?: string;
    costCalculation: string;
    investmentPercentage?: number;
    /** The row's free-text comment */
    comment?: string;
}

export interface ComponentSchedule {
    componentKey: string;
    componentType: string;
    cardinalDirection: CardinalDirection | null;
    /** null when neither the entry nor the condition gives a replacement year, or there is no lifetime */
    replacement: ResolvedReplacement | null;
    /** Type templates first in their own order, then activities on other templates */
    activities: ResolvedActivity[];
}

const MAX_REPLACEMENT_CYCLES = 100;
const MAX_OCCURRENCES = 1000;

const yearOf = (date: string): number => Number(date.slice(0, 4));

const isValidInterval = (value: number | undefined): value is number =>
    value !== undefined && value > 0 && isFinite(value);

const formatRate = (rate: number | undefined): string => (rate ?? 0).toLocaleString('sv-SE');

/** An overdue date is due now: moved to the start year on the same month and day (month-end clamped) */
export const overdueToStartYear = (date: string, startYear: number): string => {
    const year = yearOf(date);
    return year < startYear ? addInterval(date, { value: startYear - year, unit: 'years' }) : date;
};

export const resolveComponentSchedule = (options: ResolveScheduleOptions): ComponentSchedule | null => {
    const { component, entry, building, lifetime, typeTemplates, findTemplate, window, isDraft } = options;

    const componentType = component.specificType?.value;
    if (!componentType) return null;

    const cardinalDirection = resolveCardinalDirection(component, building);

    const replacement = resolveReplacement(component, entry, building, lifetime, cardinalDirection, window, isDraft);
    const replacementYears = replacement?.years ?? [];

    const activities: ResolvedActivity[] = [];
    const seen = new Set<string>();
    const entries = entry.maintenanceActivities || {};

    const resolveOne = (templateId: string, template: MaintenanceTemplate | undefined): void => {
        if (seen.has(templateId)) return;
        seen.add(templateId);
        if (entry.removedActivities?.[templateId]) return; // removed from this component by the user
        const activity = entries[templateId];
        if (activity?.disabled === true) return;
        if (!template) return; // template deleted since the entry was written
        if (!activity && !isDraft) return; // an active plan is only what was activated

        const resolved = resolveActivity(
            component, template, activity ?? {}, building, lifetime, cardinalDirection, replacementYears, window, isDraft
        );
        if (resolved) activities.push(resolved);
    };

    // The type's own templates keep their order (chart colours follow it); in a
    // draft one without an entry is scheduled with defaults, as the card shows it.
    typeTemplates.forEach(template => {
        if (!template?.id) return;
        if (!entries[template.id] && !templateOfferedTo(template, building.key)) return;
        resolveOne(template.id, template);
    });
    Object.keys(entries).forEach(templateId => {
        resolveOne(templateId, typeTemplates.find(t => t.id === templateId) ?? findTemplate(templateId));
    });

    return {
        componentKey: component.key,
        componentType,
        cardinalDirection,
        replacement,
        activities
    };
};

const resolveReplacement = (
    component: BaseComponentImpl,
    entry: ComponentMaintenanceData,
    building: Building,
    lifetime: ComponentLifetime | undefined,
    cardinalDirection: CardinalDirection | null,
    window: ScheduleWindow,
    isDraft: boolean
): ResolvedReplacement | null => {
    if (!lifetime?.expectedLifetimeYears) return null;

    const directional = getLifetimeForDirection(lifetime, cardinalDirection);
    const expectedLifetimeYears = directional.expectedLifetimeYears;
    if (!isValidInterval(expectedLifetimeYears)) {
        console.warn('Invalid expected lifetime for component:', component.key, 'lifetime:', expectedLifetimeYears);
        return null;
    }

    const storedYear = entry.replacementYear;
    const resolvedYear = storedYear !== undefined && storedYear !== null && isFinite(storedYear)
        ? storedYear
        : calculateReplacementDate(component, lifetime, building)?.getFullYear();
    if (resolvedYear === undefined) return null;

    // Overdue: the replacement is due now, not silently dropped
    const firstYear = Math.max(resolvedYear, window.startYear);

    const years: number[] = [];
    for (let year = firstYear, cycle = 0; year < window.endYear && cycle < MAX_REPLACEMENT_CYCLES; year += expectedLifetimeYears, cycle++) {
        years.push(year);
    }

    const cost = getReplacementDisplayCost(entry, component, lifetime, building, isDraft);
    const costSource: CostSource = entry.replacementCostSource || 'template';
    const rate = customRateOf(costSource, entry.replacementCustomUnitPrice) ?? directional.replacementCost;
    const costCalculation = costSource === 'custom'
        ? `${cost.toLocaleString('sv-SE')} kr`
        : lifetime.costUnit === 'per_sqm'
            ? `${formatRate(rate)} kr/m² × ${component.totalArea(building)?.toLocaleString('sv-SE')} m²`
            : lifetime.costUnit === 'per_lm'
                ? `${formatRate(rate)} kr/lm × ${component.linearMeters?.value?.toLocaleString('sv-SE')} lm`
                : `${cost.toLocaleString('sv-SE')} kr`;

    return {
        years,
        firstYear,
        expectedLifetimeYears,
        cost,
        costSource,
        costComment: entry.replacementCostComment,
        costCalculation,
        investmentPercentage: entry.replacementInvestmentPercentage
    };
};

const resolveActivity = (
    component: BaseComponentImpl,
    template: MaintenanceTemplate,
    activity: Partial<ComponentMaintenanceData['maintenanceActivities'][string]>,
    building: Building,
    lifetime: ComponentLifetime | undefined,
    cardinalDirection: CardinalDirection | null,
    replacementYears: number[],
    window: ScheduleWindow,
    isDraft: boolean
): ResolvedActivity | null => {
    const templateInterval = getIntervalForDirection(template, cardinalDirection);
    const intervalValue = isValidInterval(activity.intervalValue) ? activity.intervalValue : templateInterval.interval_value;
    const intervalType = activity.intervalType || templateInterval.interval_type;
    if (!isValidInterval(intervalValue)) {
        console.error('Invalid interval value:', intervalValue, 'for component:', component.key);
        return null;
    }

    let firstDate = activity.nextDate;
    if (!firstDate && lifetime) {
        const computed = calculateNextMaintenanceDate(component, template, lifetime, building);
        firstDate = formatDateToString(computed || new Date());
    }
    if (!firstDate || !/^\d{4}-\d{2}-\d{2}$/.test(firstDate)) return null;
    firstDate = overdueToStartYear(firstDate, window.startYear);

    // Every occurrence is anchored on the first date (k·interval), so a month-end
    // cadence does not drift through repeated clamps. A replacement year gets no
    // maintenance and the schedule restarts, anchored on the replacement.
    const dates: string[] = [];
    let anchor = firstDate;
    let step = 0;
    let date = firstDate;
    for (let i = 0; i < MAX_OCCURRENCES && yearOf(date) < window.endYear; i++) {
        const year = yearOf(date);
        if (year < 1900 || year > 2200) {
            console.error('Year out of bounds:', year, 'for component:', component.key);
            break;
        }
        if (year >= window.startYear && !replacementYears.includes(year)) {
            dates.push(date);
        }
        const next = addInterval(anchor, { value: intervalValue * (step + 1), unit: intervalType });
        const replacementBeforeNext = replacementYears.find(r => r > year && r <= yearOf(next));
        if (replacementBeforeNext !== undefined) {
            anchor = `${replacementBeforeNext}-01-01`;
            step = 1;
            date = addInterval(anchor, { value: intervalValue, unit: intervalType });
        } else {
            step++;
            date = next;
        }
    }

    const fullActivity = { nextDate: firstDate, intervalType, intervalValue, lastUpdated: '', ...activity };
    const cost = getActivityDisplayCost(fullActivity, component, template, building, isDraft);
    const costSource: CostSource = activity.costSource || 'template';
    const rate = customRateOf(costSource, activity.customUnitPrice) ?? template.cost;
    let costCalculation: string;
    if (costSource === 'custom') {
        costCalculation = `${cost.toLocaleString('sv-SE')} kr`;
    } else if (template.cost_unit === 'per_sqm') {
        const area = activity.customQuantity !== undefined && activity.customQuantity > 0
            ? activity.customQuantity
            : component.totalArea(building);
        costCalculation = `${formatRate(rate)} kr/m² × ${area?.toLocaleString('sv-SE')} m²`;
    } else if (template.cost_unit === 'per_lm') {
        const length = activity.customQuantity !== undefined && activity.customQuantity > 0
            ? activity.customQuantity
            : component.linearMeters?.value;
        costCalculation = `${formatRate(rate)} kr/lm × ${length?.toLocaleString('sv-SE')} lm`;
    } else if (activity.customUnitCount !== undefined) {
        costCalculation = `${formatRate(rate)} kr/st × ${unitCountOf(activity.customUnitCount).toLocaleString('sv-SE')} st`;
    } else {
        costCalculation = `${cost.toLocaleString('sv-SE')} kr`;
    }

    return {
        templateId: template.id,
        template,
        intervalValue,
        intervalType,
        firstDate,
        dates,
        cost,
        costSource,
        costComment: activity.costComment,
        costCalculation,
        investmentPercentage: activity.investmentPercentage,
        comment: activity.comment
    };
};

/** Flatten a by-type template map into an id lookup, for callers that only hold `TemplatesByType`. */
export const templateLookup = (templatesByType: Record<string, MaintenanceTemplate[]>): ((id: string) => MaintenanceTemplate | undefined) => {
    const byId = new Map<string, MaintenanceTemplate>();
    Object.values(templatesByType).forEach(templates => {
        (Array.isArray(templates) ? templates : []).forEach(template => {
            if (template?.id) byId.set(template.id, template);
        });
    });
    return id => byId.get(id);
};
